import { watch } from 'vue'

import { useCartStore } from '@/stores/cart'
import { useCatalogStore } from '@/stores/catalog'

import { type RewardTier, RewardTierState } from '@/types/RewardTier'

export interface RewardAddedCrossing {
  /** True when the crossing unlocked the final tier (nothing left to approach). */
  allUnlocked: boolean
  /** The tier the customer is now working toward, or `null` once all are unlocked. */
  nextReward: RewardTier | null
  /**
   * **Every** tier this mutation unlocked, ascending by threshold — never empty on an `onAdded`.
   *
   * This used to be computed and then discarded in favour of `nextReward` alone, which was lossy in
   * the one direction that matters: a single add can cross several tiers, and collapsing to "what's
   * next" threw away *what just happened*. `RewardSequenceInput.unlockedTiers` is fed from here, and
   * the celebration is keyed on the last entry (business ruling — a multi-tier crossing celebrates
   * once, for the highest tier reached).
   */
  unlockedTiers: RewardTier[]
  /**
   * Effective subtotal before the mutation. Exposed because the tracker's rail fill is a function of
   * the subtotal, so computing the sequence's `fromPct` means re-stamping the ladder at the *previous*
   * value — and this watcher is the only place that still knows it.
   */
  previousSubtotal: number
  /** Effective subtotal after the mutation, i.e. what the ladder is currently stamped against. */
  subtotal: number
}

export interface RewardRemovedCrossing {
  /** The furthest reward that dropped back to locked. */
  removedReward: RewardTier
  /** Dollars needed to re-cross that reward's threshold. */
  reclaimAmount: number
}

export interface UseRewardThresholdCrossingOptions {
  onAdded: (crossing: RewardAddedCrossing) => void
  onRemoved: (crossing: RewardRemovedCrossing) => void
}

/**
 * Watches the cart's effective subtotal and the reward ladder and invokes the supplied callbacks
 * when a **cart mutation** crosses a reward threshold — `onAdded` when one or more tiers newly
 * unlock, `onRemoved` when one or more drop back to locked. Detection only: the caller decides what a
 * crossing *means*, which today is a toast off an excluded route and the unlock sequence on an
 * enhanced one. Keeping it detection-only is what lets both hang off one watcher instead of two.
 *
 * Fires on cart mutations only: the baseline re-initializes **silently** on first run and whenever
 * the tier set changes (e.g. the catalog finishes loading), so neither hydration nor catalog load
 * produces a toast — even when the ladder populates in the same reactive flush as a subtotal
 * change. Per direction it reports a single crossing — for `onAdded` the tier now being approached;
 * for `onRemoved` the furthest tier that re-locked.
 */
export function useRewardThresholdCrossing(options: UseRewardThresholdCrossingOptions): void {
  const cartStore = useCartStore()
  const catalogStore = useCatalogStore()

  const effectiveSubtotal = (): number => cartStore.effectiveSubtotal

  const ladderSignature = (): string =>
    catalogStore.rewardLadder.map((tier) => tier.promotionId).join(',')

  let previousSubtotal: number | null = null
  let previousUnlockedIds = new Set<string>()
  let previousLadderSignature: string | null = null

  watch(
    // Re-runs on a subtotal change (cart mutation) or a tier-set change (catalog / proof-type load).
    // The tier-set part reads the cached, subtotal-independent `rewardLadder`.
    () => `${effectiveSubtotal()}:${ladderSignature()}`,
    () => {
      const subtotal = effectiveSubtotal()
      const signature = ladderSignature()
      const tiers = catalogStore.rewardTiers(subtotal)
      // Shared definition of "which tiers are unlocked" — same getter the crossing guard compares.
      const unlockedIds = new Set(catalogStore.unlockedRewardTierIds(subtotal))

      // Baseline silently on first run, when the subtotal is unchanged, and whenever the tier set
      // itself changed (e.g. catalog / proof-type load) — never a toast. A crossing is only
      // meaningful when both compared subtotals are measured against the SAME ladder, so a
      // ladder-composition change is never a user crossing; comparing the new unlocked set against
      // a stale (often empty) baseline would otherwise fire a spurious "reward unlocked" toast when
      // the ladder populates in the same reactive flush as a subtotal change.
      if (
        previousSubtotal === null ||
        subtotal === previousSubtotal ||
        signature !== previousLadderSignature
      ) {
        previousSubtotal = subtotal
        previousUnlockedIds = unlockedIds
        previousLadderSignature = signature
        return
      }

      const previous = previousUnlockedIds
      // Captured before the baseline moves on; the guard above proves it is non-null here.
      const priorSubtotal = previousSubtotal
      previousSubtotal = subtotal
      previousUnlockedIds = unlockedIds
      previousLadderSignature = signature

      // Threshold-cross toasts are part of the expanded-tracker experience only. Legacy-bar catalogs
      // (reward promotions without expanded `rewardTracker*` content) keep the generic add/remove cart
      // toasts instead, so don't fire reward toasts there. The baseline above still tracks crossings,
      // so detection stays correct if expanded content becomes available.
      if (!catalogStore.isExpandedRewardLadder) return

      const newlyUnlocked = tiers.filter(
        (tier) => unlockedIds.has(tier.promotionId) && !previous.has(tier.promotionId)
      )
      if (newlyUnlocked.length > 0) {
        options.onAdded({
          allUnlocked: tiers.every((tier) => tier.state === RewardTierState.Unlocked),
          nextReward: tiers.find((tier) => tier.state === RewardTierState.Current) ?? null,
          // `tiers` is threshold-ordered, so the filter that produced this preserved that order and
          // the last entry is the highest tier crossed — which is the one that celebrates.
          unlockedTiers: newlyUnlocked,
          previousSubtotal: priorSubtotal,
          subtotal
        })
        return
      }

      const newlyRemoved = tiers.filter(
        (tier) => !unlockedIds.has(tier.promotionId) && previous.has(tier.promotionId)
      )
      if (newlyRemoved.length > 0) {
        const removedReward = newlyRemoved.reduce((furthest, tier) =>
          tier.minimumSubtotal > furthest.minimumSubtotal ? tier : furthest
        )
        options.onRemoved({
          removedReward,
          reclaimAmount: Math.max(0, removedReward.minimumSubtotal - subtotal)
        })
      }
    },
    { immediate: true }
  )
}
